import * as React from 'react';

import { cn } from '../utils';

export type TextareaProps = React.ComponentProps<'textarea'> & {
  /** Grow with the content between these row counts, then scroll. */
  autoGrow?: { minRows: number; maxRows: number };
  /**
   * For a one-line value that is too long to read in an input, such as a name or a URL: it wraps
   * on screen, Enter submits the form instead of adding a line break, and pasted line breaks
   * become spaces.
   */
  singleLine?: boolean;
};

const textareaClassName =
  'border-input placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-12 w-full rounded-md border bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50';

/** The opt-in behaviour behind `autoGrow` and `singleLine`. A plain Textarea never renders this. */
const EnhancedTextarea = ({
  className,
  autoGrow,
  singleLine,
  ref,
  onKeyDown,
  onChange,
  ...props
}: TextareaProps) => {
  const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

  const setRefs = React.useCallback(
    (element: HTMLTextAreaElement | null) => {
      innerRef.current = element;
      if (typeof ref === 'function') ref(element);
      else if (ref) ref.current = element;
    },
    [ref],
  );

  const minRows = autoGrow?.minRows;
  const maxRows = autoGrow?.maxRows;
  const resize = React.useCallback(() => {
    const element = innerRef.current;
    if (!element || minRows === undefined || maxRows === undefined) return;

    const style = window.getComputedStyle(element);
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.25;
    const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const border = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);

    element.style.height = 'auto';
    const contentHeight = element.scrollHeight - padding;
    const rows = Math.min(maxRows, Math.max(minRows, Math.ceil(contentHeight / lineHeight)));
    element.style.height = `${rows * lineHeight + padding + border}px`;
    element.style.overflowY = contentHeight > maxRows * lineHeight ? 'auto' : 'hidden';
  }, [minRows, maxRows]);

  React.useLayoutEffect(resize, [resize, props.value]);

  return (
    <textarea
      ref={setRefs}
      rows={autoGrow?.minRows}
      className={cn(textareaClassName, autoGrow && 'min-h-0 resize-none', className)}
      onKeyDown={
        singleLine
          ? (event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                event.currentTarget.form?.requestSubmit();
              }
              onKeyDown?.(event);
            }
          : onKeyDown
      }
      onChange={(event) => {
        if (singleLine && /[\r\n]/.test(event.target.value)) {
          event.target.value = event.target.value.replace(/[\r\n]+/g, ' ');
        }
        onChange?.(event);
        resize();
      }}
      {...props}
    />
  );
};

const Textarea = ({ className, autoGrow, singleLine, ...props }: TextareaProps) => {
  if (autoGrow || singleLine) {
    return (
      <EnhancedTextarea
        className={className}
        autoGrow={autoGrow}
        singleLine={singleLine}
        {...props}
      />
    );
  }
  return <textarea className={cn(textareaClassName, className)} {...props} />;
};

Textarea.displayName = 'Textarea';

export { Textarea };
